import joi from 'joi';
import React, { useCallback } from 'react';
import { Alert, Box, Button, DialogActions, DialogContent, Theme } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { QlfCodeArea } from '@qualifio/ui-commons/dist/components/qlf-code-area';
import { defaultTemplate, parseRSSFeed } from '@loyalty/editor-components';
import { extractErrorMessage } from '@loyalty/shared-common';
import { makeStyles } from '@mui/styles';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { EditorModalContentProps } from '../../../components/EditorModal';
import { Loader } from '../../../../shared/layout/Loader';
import { useJoiResolver } from '../../../../shared/forms/use-joi-resolver';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  code: {
    height: '55vh',
  },
}));

interface FormValue {
  template: string;
}

const $form = joi.object({
  template: joi.string(),
});

export interface RSSWidgetModalProps extends EditorModalContentProps {
  template?: string;
  feedUrl?: string;
  callback: (data: string) => void;
}

export function RSSWidgetModal({
  template,
  feedUrl,
  callback,
  closeModal,
}: RSSWidgetModalProps): React.ReactElement {
  const classes = useStyles();
  const { t } = useTranslation();
  const resolver = useJoiResolver($form);
  const form = useForm<FormValue>({
    defaultValues: { template },
    resolver,
  });

  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
  } = form;

  const onSubmit = useCallback((values: FormValue) => {
    callback(values.template);
    closeModal();
  }, []);

  const feed = useQuery({
    queryKey: ['rss-feed-str', feedUrl],

    queryFn: async () => {
      if (!feedUrl) {
        return '';
      }
      const json = await parseRSSFeed(feedUrl);
      return json ? JSON.stringify(json, null, 2) : '';
    },
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit, console.error)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ textAlign: 'left' }}>
            <h2>{t('widgets.rss.modal.title')}</h2>
          </Box>
          <Box sx={{ display: 'flex', flexGrow: 1 }}>
            <Box
              sx={{
                height: '100%',
                width: '50%',
                display: 'flex',
                flexDirection: 'column',
                marginRight: '10px',
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <h3>{t('widgets.rss.modal.data.title')}</h3>
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                {feed.isPending ? (
                  <Loader />
                ) : feed.error ? (
                  <Alert severity="error">
                    {t('widgets.rss.errors.feedURL.parse', {
                      message: extractErrorMessage(feed.error as Error),
                    })}
                  </Alert>
                ) : (
                  <QlfCodeArea
                    value={
                      !feedUrl || feedUrl === '' ? t('widgets.rss.errors.feedURL.empty') : feed.data
                    }
                    className={classes.code}
                    allowFullScreen
                    defaultLanguage="json"
                    height="100%"
                    options={{
                      readOnly: true,
                    }}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ height: '100%', width: '50%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ textAlign: 'left' }}>
                <h3>{t('widgets.rss.modal.template.title')}</h3>
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Controller
                  control={control}
                  name="template"
                  render={({ field: { ref, value, ...field } }) => (
                    <QlfCodeArea
                      {...field}
                      value={value as string}
                      className={classes.code}
                      defaultValue={defaultTemplate}
                      allowFullScreen
                      defaultLanguage=""
                      height="100%"
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            color="grey"
            onClick={() => {
              closeModal();
            }}
          >
            {t('generic.actions.cancel')}
          </Button>
          <Button color="success" disabled={!isDirty} type="submit">
            {t('editor.embedded-media.modal.apply')}
          </Button>
        </DialogActions>
      </form>
    </FormProvider>
  );
}
